import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar } from 'lucide-react';
import { blogPosts } from '../../data/blogPosts';
import { Button } from '../ui/button';

export function LatestBlogPosts() {
  const categories = ['Tecnología', 'Noticias', 'Guías', 'Legal'];

  const displayedPosts = categories
    .map((category) =>
      blogPosts.find(
        (post) =>
          post.category.toLowerCase() === category.toLowerCase() ||
          (category === 'Tecnología' && post.category.includes('Tecn')) ||
          (category === 'Noticias' && post.category.includes('Noti')) ||
          (category === 'Guías' && post.category.includes('Guía')) ||
          (category === 'Legal' && post.category.includes('Legal'))
      )
    )
    .filter(Boolean);

  if (displayedPosts.length < 3) {
    const existingIds = new Set(displayedPosts.map((post) => post?.id));
    const remainingPosts = blogPosts
      .filter((post) => !existingIds.has(post.id))
      .slice(0, 4 - displayedPosts.length);
    displayedPosts.push(...remainingPosts);
  }

  const [heroPost, ...otherPosts] = displayedPosts.slice(0, 4);

  return (
    <section className="py-28 bg-[var(--zw-ivory)] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 right-[8%] h-56 w-56 rounded-full bg-[#ece1cf]/55 blur-3xl" />
      </div>

      <div className="home-shell">
        <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-6 mb-12">
          <div className="max-w-3xl">
            <motion.span
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="home-kicker"
            >
              Ultimas novedades
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.05 }}
              className="home-title mb-5"
            >
              Guias, consejos y novedades para sacar mas partido a tu Zwheel
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="home-copy"
            >
              Descubre contenidos sobre tecnologia, normativa, mantenimiento y actualidad para moverte mejor y cuidar tu patinete.
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.14 }}
          >
            <Button asChild variant="outline" className="rounded-full px-6 bg-white">
              <Link to="/blog">
                Ver todo el blog
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[1.05fr_0.95fr] gap-6">
          {heroPost && (
            <motion.article
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="group overflow-hidden rounded-[2rem] bg-white shadow-[0_22px_80px_rgba(0,0,0,0.06)] border border-black/5"
            >
              <Link to={`/blog/${heroPost.slug}`} className="grid grid-cols-1 md:grid-cols-[1.05fr_0.95fr] min-h-[520px]">
                <div className="relative overflow-hidden">
                  <img
                    src={heroPost.image || '/placeholder-blog.jpg'}
                    alt={heroPost.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent md:bg-gradient-to-r md:from-transparent md:via-transparent md:to-transparent" />
                </div>

                <div className="p-8 md:p-10 lg:p-12 flex flex-col justify-between">
                  <div>
                    <span className="inline-flex rounded-full bg-[var(--zw-sand)] px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-800 mb-5">
                      {heroPost.category || 'Blog'}
                    </span>
                    <h3 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-5">{heroPost.title}</h3>
                    <p className="text-lg text-gray-600 leading-relaxed mb-6">{heroPost.excerpt}</p>
                  </div>

                  <div className="flex flex-col gap-4">
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="w-4 h-4 mr-2" />
                      {heroPost.publishDate}
                      {heroPost.author && <span className="ml-3">• {heroPost.author}</span>}
                    </div>
                    <span className="inline-flex items-center text-sm font-semibold text-[var(--zw-moss)]">
                      Leer articulo
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.article>
          )}

          <div className="grid grid-cols-1 gap-6">
            {otherPosts.map((post, index) => (
              <motion.article
                key={post?.id || index}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: index * 0.07 }}
                className="group overflow-hidden rounded-[1.75rem] bg-white border border-black/5 shadow-[0_16px_55px_rgba(0,0,0,0.05)]"
              >
                <Link to={`/blog/${post?.slug}`} className="grid grid-cols-[0.95fr_1.05fr] min-h-[220px]">
                  <div className="relative overflow-hidden">
                    <img
                      src={post?.image || '/placeholder-blog.jpg'}
                      alt={post?.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>

                  <div className="p-6 flex flex-col justify-between">
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500 mb-3">
                        {post?.category || 'Blog'}
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 leading-tight mb-3">{post?.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed line-clamp-3">{post?.excerpt}</p>
                    </div>
                    <div className="pt-5">
                      <div className="flex items-center text-xs text-gray-500 mb-4">
                        <Calendar className="w-3 h-3 mr-1.5" />
                        {post?.publishDate}
                      </div>
                      <span className="inline-flex items-center text-sm font-semibold text-[var(--zw-moss)]">
                        Leer mas
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        </div>

        <div className="mt-8 text-center xl:hidden">
          <Button asChild variant="outline" className="rounded-full px-6 bg-white">
            <Link to="/blog">Ver todo el blog</Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
